/**
 * layout — a small, dependency-free layered layout for the story graph.
 *
 * @xyflow positions nodes by absolute (x, y); `buildStoryGraph` returns no
 * geometry, so we assign one here. The algorithm is a classic Sugiyama-lite:
 *
 *   1. Layer assignment by longest path from a source. Roots (no incoming
 *      edge) sit in layer 0; every other node lands one layer past the
 *      deepest predecessor. Cycles (LS can loop scenes) are broken by a
 *      visited-guard so we never spin.
 *   2. Within a layer, nodes keep first-seen order (stable, deterministic) so
 *      the canvas doesn't reshuffle between renders of the same work.
 *
 * Left-to-right reading: layers march along +x, siblings stack along +y. This
 * is intentionally simple — good enough to make branching structure legible
 * without pulling in a heavy graph-layout dependency.
 */
import type { StoryGraph } from "../../ls/build-graph.js";

/** A laid-out position for one node. */
export interface NodePosition {
  x: number;
  y: number;
}

/** Tuning for the grid the layout snaps to. */
export interface LayoutOptions {
  /** Horizontal gap between layer columns (px). @default 320 */
  readonly columnGap?: number;
  /** Vertical gap between sibling rows within a column (px). @default 132 */
  readonly rowGap?: number;
  /**
   * Reading direction. "LR" (default) marches layers along +x (story map);
   * "TB" marches layers along +y (a top-down step flow) — siblings then spread
   * along +x. Same layering maths, transposed geometry.
   */
  readonly direction?: "LR" | "TB";
}

const DEFAULT_COLUMN_GAP = 320;
const DEFAULT_ROW_GAP = 132;

/**
 * Compute an `id → {x, y}` map for every node in the graph.
 *
 * Deterministic and total: every node gets a position, isolated nodes included
 * (they collect in layer 0). Never throws on cycles or dangling edges.
 */
export function layoutGraph(graph: StoryGraph, options: LayoutOptions = {}): Map<string, NodePosition> {
  const columnGap = options.columnGap ?? DEFAULT_COLUMN_GAP;
  const rowGap = options.rowGap ?? DEFAULT_ROW_GAP;

  const nodeIds = graph.nodes.map((n) => n.id);
  const idSet = new Set(nodeIds);

  // Adjacency + in-degree over edges that connect two real nodes.
  const outgoing = new Map<string, string[]>();
  const indegree = new Map<string, number>();
  for (const id of nodeIds) {
    outgoing.set(id, []);
    indegree.set(id, 0);
  }
  for (const e of graph.edges) {
    if (!idSet.has(e.source) || !idSet.has(e.target)) continue;
    if (e.source === e.target) continue; // self-loops don't affect layering
    outgoing.get(e.source)!.push(e.target);
    indegree.set(e.target, (indegree.get(e.target) ?? 0) + 1);
  }

  // Layer assignment: longest path from any root. Roots = in-degree 0; if the
  // graph is all-cycles (no root), the first node seeds layer 0 so we still
  // terminate.
  const layer = new Map<string, number>();
  for (const id of nodeIds) layer.set(id, 0);

  const roots = nodeIds.filter((id) => (indegree.get(id) ?? 0) === 0);
  const seeds = roots.length > 0 ? roots : nodeIds.slice(0, 1);

  // BFS/relax: push a node's layer forward when reached via a longer path.
  // The `guard` bounds total relaxations to avoid pathological cycle spins.
  const queue: string[] = [...seeds];
  let guard = nodeIds.length * nodeIds.length + nodeIds.length;
  while (queue.length > 0 && guard-- > 0) {
    const id = queue.shift()!;
    const here = layer.get(id) ?? 0;
    for (const next of outgoing.get(id) ?? []) {
      const want = here + 1;
      if (want > (layer.get(next) ?? 0)) {
        layer.set(next, want);
        queue.push(next);
      }
    }
  }

  // Order within each layer by first-seen index for stability.
  const byLayer = new Map<number, string[]>();
  for (const id of nodeIds) {
    const l = layer.get(id) ?? 0;
    if (!byLayer.has(l)) byLayer.set(l, []);
    byLayer.get(l)!.push(id);
  }

  const topDown = options.direction === "TB";
  const positions = new Map<string, NodePosition>();
  for (const [l, ids] of byLayer) {
    // Centre each layer around 0 (on the cross axis) so the diagram balances.
    const span = (ids.length - 1) * rowGap;
    ids.forEach((id, row) => {
      const along = l * columnGap; // main axis (layer depth)
      const cross = row * rowGap - span / 2; // cross axis (siblings)
      positions.set(id, topDown ? { x: cross, y: along } : { x: along, y: cross });
    });
  }
  return positions;
}
